import { gql } from '@reverbdotcom/commons/src/gql';
import React from 'react';
import I18n from 'i18n-js';
import { SignalsDataFragment } from '@reverbdotcom/commons/src/gql/graphql';
import { RCImage, RCTooltip } from '@reverbdotcom/cadence/components';
import OrangeSaleTag from '@reverbdotcom/cadence/images/nudge/orange-sale-tag.svg';
import { REVERB_OUTLET_PATH } from '@reverbdotcom/commons/src/url_helpers';
import { LISTING_STATES } from '@reverbdotcom/commons/src/constants';
import ListingNudge from './discovery/listing_nudge';

export const ICON_HEIGHT_TINY = 20;
export const ICON_HEIGHT_REGULAR = 34;

export const SignalsFragment = gql(`
  fragment SignalsData on Listing {
    _id
    id
    state
    usOutlet
    currency
    signals: signals (input: { groups: [ITEM_PAGE_WHY_TO_BUY, ITEM_PAGE_NUDGES, ITEM_PAGE_WHY_REVERB] }) {
      name
      group
      title
      subtitle
      link {
        text
        url
      }
      icon
      iconDark
      tooltipText
    }
  }
`);

interface OutletCallout {
  name: string,
  icon: string,
  iconDark?: string,
  title: string,
  subtitle: JSX.Element,
  tooltipText?: string,
  link?: {
    text?: string,
    url?: string
  }
}

export function Item2WhyReverbSignals({ listing }: SignalsProps) {
  const signals = listing.signals?.filter(signal => signal.group === 'ITEM_PAGE_WHY_REVERB');

  if (!signals?.length) { return null; }

  return (
    <div className="item2__content__section item2__content__section--why-reverb">
      <div className="item2__sidebar-module item2__sidebar-module--shaded">
        {signals.map(signal => {
          return <SignalDisplay signal={signal} inline={false} key={signal.name} />;
        })}
      </div>
    </div>
  );
}

export function Item2NudgeSignals({ listing }: SignalsProps) {
  if (listing.state !== LISTING_STATES.LIVE) { return null; }

  const signals = listing.signals?.filter(signal => signal.group === 'ITEM_PAGE_NUDGES');

  if (!signals?.length) { return null; }

  return (
    <div className="item2__sidebar-module">
      {signals.map(signal => {
        return <SignalDisplay signal={signal} inline={false} key={signal.name} />;
      })}
    </div>
  );
}

export function Item2OutletCallout({ listing }: SignalsProps) {
  if (!listing.usOutlet) { return null; }

  const outletCallout: OutletCallout = {
    name: 'outlet',
    icon: OrangeSaleTag,
    title: I18n.t('discovery.outletCallout.title'),
    subtitle: I18n.t('discovery.outletCallout.subtitle'),
    link: {
      text: I18n.t('discovery.outletCallout.linkText'),
      url: REVERB_OUTLET_PATH,
    },
  };

  return (
    <SignalDisplay signal={outletCallout} inline={false} key={outletCallout.name} linkInline />
  );
}

interface SignalsProps {
  listing: SignalsDataFragment;
  onFeaturedListing?: boolean;
}

export function Item2WhyToBuySignals({ listing, onFeaturedListing }: SignalsProps) {
  const signals = listing.signals?.filter((signal) =>
    signal.group === 'ITEM_PAGE_WHY_TO_BUY' || (onFeaturedListing && signal.name === 'RETURN_POLICY'),
  );
  if (!signals?.length) { return null; }

  return (
    <div className="mtb-2">
      {signals.map(signal => {
        return <SignalDisplay signal={signal} inline key={signal.name} />;
      })}
    </div>
  );
}

interface Signal {
  name?: string,
  group?: string,
  title?: string,
  subtitle?: string,
  tooltipText?: string,
  link?: {
    text?: string,
    url?: string
  },
  icon?: string,
  iconDark?: string
}

export function SignalDisplay({ signal, inline, linkInline = false }: { signal: Signal | OutletCallout, inline: boolean, linkInline?: boolean }) {
  if (!signal.name || !signal.icon) {
    return null;
  }

  const getIconSvg = function () {
    return (
      <RCImage
        src={signal.icon}
        darkModeSrc={signal.iconDark}
        imageBox="auto"
      />
    );
  };

  const subtitle = signal.subtitle && signal.tooltipText ?
    <RCTooltip text={signal.tooltipText} type="underline">
      {signal.subtitle}
    </RCTooltip> :
    <>{signal.subtitle}</>;

  const title = !signal.subtitle && signal.tooltipText ?
    <RCTooltip text={signal.tooltipText} type="underline">
      {signal.title}
    </RCTooltip> :
    <>{signal.title}</>;

  return (
    <ListingNudge
      title={title}
      subtitle={subtitle}
      linkText={signal.link?.text}
      linkUrl={signal.link?.url}
      inline={inline}
      icon={getIconSvg()}
      key={signal.title}
      linkInline={linkInline}
    />
  );
}
